import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../services/api/client';
import { featuresAPI } from '../services/api';
import { queryKeys } from '../hooks/queries/queryKeys';

// LocalStorage key for selected organization (shared with OrganizationContext)
const ORG_STORAGE_KEY = 'selectedOrganization';
const ORG_CHANGED_EVENT = 'organizationChanged';

interface FeatureFlags {
  // Core Features
  mediaLibrary: boolean;
  bulkOperations: boolean;
  taskGamesRoleMapping: boolean;
  inventory: boolean;
  ageVerification: boolean;

  // Messaging (SPEC-048; rides the 'channels' flag now, Spec 304 Phase 5 — see
  // docs/reference/feature-flag-aliases.md)
  helmMessagesWidget: boolean;       // DM widget on Helm dashboard
  helmWidgetActivityFeed: boolean;   // Activity feed widget

  // Discovery Widget
  helmDiscoveryWidget: boolean;      // Discovery matches widget on Helm dashboard

  // Chastity Widget
  helmChastityWidget: boolean;       // Chastity lock widget on Helm dashboard

  // Helm Dashboard Revamp
  helmProactiveInsights: boolean;
  helmPerPartnerCompliance: boolean;
  helmSubInsights: boolean;

  // Chastity Management (Spec 035)
  chastityManagement: boolean;

  // pillarNav removed — always true, PillarLayout is the only layout

  // AI Training Agent
  aiTrainingGeneration: boolean;

  // Timeline (Spec 103)
  dynamicTimeline: boolean;

  // Spec 113 — Dynamic-specific terminology
  customTerminology: boolean;

  // Switch Role Support (Spec 112)
  switchRole: boolean;

  // Safety features (Specs 102, 111)
  dropDetection: boolean;
  emergencyTimer: boolean;

  // Command feature gates (Spec 099 — org-level, default true)
  commandTraining: boolean;
  commandContracts: boolean;
  commandProtocols: boolean;
  commandProgress: boolean;
  commandPraise: boolean;
  commandConsequences: boolean;
  commandRewards: boolean;
  commandAutonomy: boolean;
  commandOrgasmBank: boolean;
  commandTravel: boolean;
  commandReviews: boolean;
  commandServiceRotations: boolean;
  commandCompliance: boolean;
  commandInsights: boolean;
  journalPrompts: boolean;
  limitReminders: boolean;

  // Additional feature gates
  negotiationTemplates: boolean;
  collaringStages: boolean;
  bodyMap: boolean;
  ldrTools: boolean;

  // Spec 128 — Unified Sharing UX
  unifiedSharing: boolean;

  // Spec 134 — Mobile Bottom Sheets + FAB
  mobileBottomSheets: boolean;

  // Feature Discovery Mode — show all Command tabs to subs even without content
  featureDiscoveryMode: boolean;
  // Command R4 — Sub Store tab (rewards, points, orgasm bank unified)
  subStore: boolean;

  // Spec 158 — Pre-signed CDN Upload Pipeline
  presignedCdnPipeline: boolean;

  // Spec 167 — Chastity Health Integration
  chastityHealth: boolean;

  // 2026-06-07 audit — surfaced so the frontend can gate these (were visible CTA → 403):
  mentorship: boolean;             // B6
  protocolLibraryV2: boolean;      // B4
  dynamicEvolutionArchive: boolean; // B5

  // Spec 178 — Biometric App Lock
  biometricAppLock: boolean;

  // Spec 192 — Universal Draggable Bottom Sheets (vaul-based, snap points)
  sheetV2Snappable: boolean;

  // Spec 235 — Sub-Initiation Request Mechanism
  subInitiationRequests: boolean;

  // Spec 243 — Helm Overview training program (OFF by default until Phase 3 content seeded)
  helmOverviewProgram: boolean;

  // Spec 266 sub-slice 1 — scene-plan per-section partner review + sign
  // ceremony. Gates PlannerTab's "Confirm plan" button between the new
  // ScenePlanConfirmFlow modal (on) and the old direct scenePlanAPI.confirm()
  // call (off) — staged rollout, off by default (1785183924076 migration).
  scenePlanLifecycleV2: boolean;

  // Spec 285 — Delete Unread DM Messages (Unsend). Gates the "Delete for
  // everyone" message-actions trigger in MessageThread.tsx. Route-enforced
  // (leader tier) independent of this flag — this only controls UI visibility.
  dmMessageUnsend: boolean;

  // Spec 311 — Milestone & Ceremony Engine. Gates the CelebrationWidget
  // dashboard widget + GET/POST /api/milestones calls. Dark launch: default
  // OFF (matches the backend feature_flags row seeded false in the migration).
  celebrationEngine: boolean;

  // Spec 312 — Negotiation Copilot. Gates the "Worth discussing" panel on
  // NegotiationsTab.tsx's revealed-negotiation view + the pre-negotiation
  // unrated-flagged banner on ChecklistPage.tsx, and both new GET
  // /api/limits/negotiations/:id/copilot and GET
  // /api/limits/checklist/unrated-flagged calls. Dark launch: default OFF
  // (matches the backend feature_flags row seeded false in the migration).
  // Acceptance criterion: flag off means ZERO behavior change -- both
  // consuming components gate on this BEFORE their query's `enabled`, not
  // just before rendering, so no call fires even if a query were somehow
  // reachable with the flag off.
  negotiationCopilot: boolean;

  // Spec 313 — Scene Composer. Gates the drag-and-drop composer catalog
  // panel, activity add/remove (PATCH /scene-plans/:id/activities), the
  // intensity-budget meter/override-reason confirm dialog, and the
  // "Discuss" card action's GET /limits/negotiations/discuss-state call.
  // Dark launch: default OFF (matches the backend feature_flags row seeded
  // false in the migration). Verified `getFrontendFeatureFlags()` actually
  // surfaces this key (the recurring gap the two prior specs hit) before
  // relying on it here — see featureFlagService.ts's sceneComposer entry.
  sceneComposer: boolean;

  // Spec 314 — Check-in Buddy Network. Gates the buddy list/designate UI
  // (Safety surface) and the "who gets notified on escalation" buddy
  // section on the check-in-start form. Purely additive/supplemental to
  // org-admin escalation (CEO decision, 2026-08-25) — never a replacement.
  // Dark launch: default OFF (matches the backend feature_flags row seeded
  // false in the migration). Verified `getFrontendFeatureFlags()` actually
  // surfaces this key before relying on it here — see featureFlagService.ts's
  // checkinBuddyNetwork entry (added proactively, this session).
  checkinBuddyNetwork: boolean;
}

/** Per-pillar tab ordering. null = use default navConfig order. */
export type TabOrder = Record<string, string[]> | null;

interface FeaturesContextType {
  features: FeatureFlags;
  loading: boolean;
  error: string | null;
  refreshFeatures: () => Promise<void>;
  /** Directly update a single feature flag — use after PATCH succeeds to avoid round-trip */
  setFeature: (key: string, value: boolean) => void;
  /** Custom tab ordering per pillar (Spec 129). null = default. */
  tabOrder: TabOrder;
  /** Update tab order locally after save */
  setTabOrder: (order: TabOrder) => void;
  /** Per tier-gated feature the org does NOT meet → its required tier (drives the upgrade prompt). Empty for qualifying orgs. */
  tierGate: Record<string, string>;
}

const defaultFeatures: FeatureFlags = {
  // Core Features - default false until enabled
  mediaLibrary: false,
  bulkOperations: false,
  taskGamesRoleMapping: false,
  inventory: false,
  ageVerification: false,

  // Messaging - default false until enabled
  helmMessagesWidget: false,
  helmWidgetActivityFeed: false,

  // Discovery Widget - default false until enabled
  helmDiscoveryWidget: false,

  // Chastity Widget - gates on chastityManagement flag
  helmChastityWidget: false,

  // Helm Dashboard Revamp - helmSubInsights default true (deployed).
  // helmProactiveInsights / helmPerPartnerCompliance (Spec 250 red-team fix):
  // must default false, NOT true — these gate the compliance-hub widget,
  // whose flags the CEO wants OFF in prod until explicit approval. Defaulting
  // true meant a failed /features fetch fell open (both flags true for the
  // rest of the session, per the `catch` block below which never resets
  // `features` off `defaultFeatures`), defeating that gate entirely. This is
  // the sole consumer of both keys (verified via repo grep) so this default
  // change is safe.
  helmProactiveInsights: false,
  helmPerPartnerCompliance: false,
  helmSubInsights: true,

  // Chastity Management - default true (deployed)
  chastityManagement: true,

  // pillarNav removed — always true, PillarLayout is the only layout

  // AI Training Agent - default false until enabled
  aiTrainingGeneration: false,

  // Timeline (Spec 103) - default false until enabled
  dynamicTimeline: false,

  // Spec 113 — Dynamic-specific terminology (default false until enabled)
  customTerminology: false,

  // Spec 112 — Switch Role Support (default false until enabled)
  switchRole: false,

  // Safety features - default false until enabled
  dropDetection: false,
  emergencyTimer: false,

  // Command feature gates - default true (existing orgs see all features)
  commandTraining: true,
  commandContracts: true,
  commandProtocols: true,
  commandProgress: true,
  commandPraise: true,
  commandConsequences: true,
  commandRewards: true,
  commandAutonomy: false,
  commandOrgasmBank: true,
  commandTravel: true,
  commandReviews: true,
  commandServiceRotations: false,
  commandCompliance: false,
  commandInsights: false,
  journalPrompts: false,
  limitReminders: false,

  // Additional feature gates - default false until enabled
  negotiationTemplates: false,
  collaringStages: false,
  bodyMap: false,
  ldrTools: false,

  // Spec 128
  unifiedSharing: false,

  // Spec 134
  mobileBottomSheets: false,

  featureDiscoveryMode: false,
  subStore: false,
  presignedCdnPipeline: false,

  // Spec 167
  chastityHealth: false,

  // 2026-06-07 audit (surfaced flags)
  mentorship: false,
  protocolLibraryV2: false,
  dynamicEvolutionArchive: false,

  // Spec 178
  biometricAppLock: false,

  // Spec 192
  sheetV2Snappable: false,

  // Spec 235
  subInitiationRequests: false,

  // Spec 243 — default false, flipped on once training content is seeded
  helmOverviewProgram: false,

  // Spec 266 sub-slice 1 — default false until staged rollout completes
  scenePlanLifecycleV2: false,

  // Spec 285 — default false (dark launch, leader tier)
  dmMessageUnsend: false,

  // Spec 311 — default false (dark launch, matches backend feature_flags seed)
  celebrationEngine: false,

  // Spec 312 — default false (dark launch, matches backend feature_flags seed)
  negotiationCopilot: false,

  // Spec 313 — default false (dark launch, matches backend feature_flags seed)
  sceneComposer: false,

  // Spec 314 — default false (dark launch, matches backend feature_flags seed)
  checkinBuddyNetwork: false,
};

const FeaturesContext = createContext<FeaturesContextType>({
  features: defaultFeatures,
  loading: true,
  error: null,
  refreshFeatures: async () => {},
  setFeature: () => {},
  tabOrder: null,
  setTabOrder: () => {},
  tierGate: {},
});

export const useFeatures = () => useContext(FeaturesContext);

interface FeaturesProviderProps {
  children: ReactNode;
}

/** Combined shape returned by one query -- see loadFeaturesData() below for
 * why this stays a single bundled fetch rather than splitting into two. */
interface FeaturesQueryData {
  features: FeatureFlags;
  tierGate: Record<string, string>;
  tabOrder: TabOrder;
}

// Get the current org's public_id from localStorage. Module-level (not a
// hook) so it can serve both as useState's lazy initializer and the
// organizationChanged handler below without a useCallback identity dance.
function getCurrentOrgId(): string | null {
  try {
    const stored = localStorage.getItem(ORG_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed?.id || null;  // Organization.id is the public_id
    }
  } catch {
    // Ignore parse errors
  }
  return null;
}

/** queryFn: fetches features + tab order in ONE Promise.all, exactly mirroring
 * pre-migration loadFeatures() -- this is a data-layer swap, not a
 * decomposition into two queries. Both responses are consumed together by
 * every caller and there is no independent loading/error UI for tab order,
 * so splitting would only add a second cache entry with no behavioral
 * benefit. */
async function loadFeaturesData(orgId: string | null): Promise<FeaturesQueryData> {
  try {
    const [featuresResponse, tabOrderResponse] = await Promise.all([
      featuresAPI.getFeatures(orgId || undefined),
      // Tab order requires auth + org — only fetch when we have an org
      orgId
        ? api.get(`/organizations/${orgId}/tab-order`).catch(() => null)
        : Promise.resolve(null),
    ]);

    // Always start from defaults, then overlay the API response. This
    // ensures keys NOT returned by the API reset to defaults (e.g. when a
    // feature override is pruned because it matches the default) -- see the
    // Spec 250 helmProactiveInsights/helmPerPartnerCompliance comment on
    // defaultFeatures above for why skipping this reset is a real prior bug
    // class, not hypothetical. Previous pattern ({ ...prev, ...data })
    // caused stale state when toggling features back to their default value.
    const apiFeatures = featuresResponse.data?.data ?? {};

    return {
      features: { ...defaultFeatures, ...apiFeatures },
      // tierGate: per tier-gated surface the org doesn't meet → required
      // tier (drives the upgrade prompt).
      tierGate: (featuresResponse.data as { tierGate?: Record<string, string> })?.tierGate ?? {},
      tabOrder: tabOrderResponse?.data?.data || null,
    };
  } catch (err) {
    console.error('Failed to load feature flags:', err);
    throw err;
  }
}

export const FeaturesProvider: React.FC<FeaturesProviderProps> = ({ children }) => {
  const queryClient = useQueryClient();
  // orgId is React state (not read fresh from localStorage on every render)
  // so it can drive the query key directly -- a genuine org switch produces
  // a new key and useQuery fetches it automatically, with no manual
  // invalidate/refetch call needed. See the organizationChanged listener
  // below for how this state gets updated.
  const [orgId, setOrgId] = useState<string | null>(() => getCurrentOrgId());

  const query = useQuery<FeaturesQueryData>({
    queryKey: queryKeys.features.all(orgId),
    queryFn: () => loadFeaturesData(orgId),
    // Pre-migration loadFeatures() was a single try/catch with no retry
    // wrapper -- opting out explicitly rather than inheriting the global
    // QueryClient's retry:2 default, same as PushNotificationContext.tsx.
    retry: false,
    // Pre-migration code only ever (re-)loaded on mount, an explicit
    // refreshFeatures() call, or the organizationChanged event -- never on
    // window focus or network reconnect. Matching that exactly rather than
    // silently inheriting the global QueryClient defaults (refetchOnWindowFocus:
    // true): a flag/tier changing server-side while a tab sits unfocused is a
    // real staleness window, but fixing it is a deliberate cadence
    // improvement, not something to ship as a side effect of this migration
    // (Spec 292's rule is "no silent cadence change," not "silently improve
    // cadence"). Revisit explicitly if stale-flag staleness becomes a real
    // complaint.
    // staleTime: Infinity -- there's exactly one long-lived observer for this
    // query (FeaturesProvider mounts once at App.tsx's root), so nothing
    // should implicitly refetch it on remount/reconnect. Every fetch this
    // query ever does is driven explicitly: initial mount, the
    // organizationChanged handler's queryKey change below, or
    // refreshFeatures()'s invalidateQueries() call.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // Refresh features when organization changes. Setting orgId to the SAME
  // value is a no-op re-render (React bails out on an unchanged primitive
  // via Object.is) -- this is what replaces the pre-migration lastOrgIdRef
  // "only reload if actually changed" comparison. Setting it to a genuinely
  // NEW value changes the query key above, which useQuery picks up and
  // fetches automatically -- no manual invalidateQueries needed here.
  //
  // CAVEAT (verified empirically, not just read from source): lib/queryClient.ts
  // has its own pre-existing organizationChanged listener that unconditionally
  // calls queryClient.removeQueries() on every dispatch, regardless of
  // whether orgId actually changed -- and OrganizationContext.tsx does have a
  // real same-org dispatch path (a server-side role change on the CURRENT
  // org, see its loadOrganizations()). That global removeQueries() destroys
  // this query's cache entry and DOES cause the active observer here to
  // rebuild and refetch (confirmed via a throwaway test against the real
  // queryClient singleton: one extra GET /features per same-org dispatch).
  // The dedup above still prevents THIS FILE's own logic from requesting a
  // reload on a same-org event, but it can no longer prevent the fetch
  // outright now that this data lives in react-query's cache -- every other
  // already-migrated org-scoped query in the app has the same exposure, this
  // migration just newly brings FeaturesContext under it (pre-migration its
  // data lived in plain component state, entirely outside that listener's
  // reach). Worth a deliberate look during OrganizationContext (Checkpoint 2)
  // rather than fixing piecemeal per-file.
  useEffect(() => {
    const handleOrgChanged = () => setOrgId(getCurrentOrgId());
    window.addEventListener(ORG_CHANGED_EVENT, handleOrgChanged);
    return () => {
      window.removeEventListener(ORG_CHANGED_EVENT, handleOrgChanged);
    };
  }, []);

  const data = query.data;
  const features = data?.features ?? defaultFeatures;
  const tierGate = data?.tierGate ?? {};
  const tabOrder = data?.tabOrder ?? null;

  // `loading` deliberately does NOT track every background refetch the way
  // a plain `query.isFetching` would. App.tsx's PrivateRoute (and a couple
  // of pages) gate their ENTIRE render on `featuresLoading` -- pre-migration,
  // loading was true only for the very first load and an explicit
  // refreshFeatures() call; the organizationChanged handler called
  // loadFeatures() directly without ever touching loading, so switching orgs
  // was silent. Because orgId is part of the query key, an org switch is
  // structurally indistinguishable from "first load for this key" via
  // isFetching/isPending alone -- so a plain isFetching mapping would flash
  // a full-page skeleton on every org switch, a real regression this
  // migration must not introduce. hasLoadedOnceRef tracks "has ANY fetch for
  // this provider ever settled" to reproduce the old silent-org-switch
  // behavior; isRefreshing brackets refreshFeatures() explicitly, matching
  // its pre-migration setLoading(true)/loadFeatures()/(finally) shape.
  const hasLoadedOnceRef = useRef(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  useEffect(() => {
    if (query.isSuccess || query.isError) {
      hasLoadedOnceRef.current = true;
    }
  }, [query.isSuccess, query.isError, orgId]);
  const loading = (!hasLoadedOnceRef.current && query.isFetching) || isRefreshing;

  const error = query.isError ? 'Failed to load feature configuration' : null;

  const refreshFeatures = useCallback(async () => {
    setIsRefreshing(true);
    // queryClient.invalidateQueries() delegates to refetchQueries(), which
    // wraps each matched query's fetch in `promise.catch(noop)` unless
    // `throwOnError` is passed (@tanstack/query-core queryClient.js) — not
    // passed here or in this app's QueryClient defaultOptions
    // (lib/queryClient.ts). Source-verified against the installed
    // @tanstack/react-query 5.102.8: this call provably never rejects, so a
    // catch here would be error handling for an impossible scenario
    // (CLAUDE.md Simplicity First).
    // missing-catch exempt: invalidateQueries() never rejects without throwOnError (see above)
    try {
      await queryClient.invalidateQueries({ queryKey: queryKeys.features.all(orgId) });
    } finally {
      setIsRefreshing(false);
    }
  }, [queryClient, orgId]);

  /** Directly patch a single flag in the cached query data — used after a
   * PATCH succeeds elsewhere in the app, to avoid a round-trip refetch.
   * Falls back to defaultFeatures/{}/null when no cache entry exists yet
   * (mirrors pre-migration setFeature, which patched the `features` state
   * that always started from defaultFeatures on mount). */
  const setFeature = useCallback((key: string, value: boolean) => {
    queryClient.setQueryData<FeaturesQueryData>(queryKeys.features.all(orgId), (old: FeaturesQueryData | undefined) => ({
      features: { ...(old?.features ?? defaultFeatures), [key]: value },
      tierGate: old?.tierGate ?? {},
      tabOrder: old?.tabOrder ?? null,
    }));
  }, [queryClient, orgId]);

  const setTabOrder = useCallback((order: TabOrder) => {
    queryClient.setQueryData<FeaturesQueryData>(queryKeys.features.all(orgId), (old: FeaturesQueryData | undefined) => ({
      features: old?.features ?? defaultFeatures,
      tierGate: old?.tierGate ?? {},
      tabOrder: order,
    }));
  }, [queryClient, orgId]);

  return (
    <FeaturesContext.Provider value={{ features, loading, error, refreshFeatures, setFeature, tabOrder, setTabOrder, tierGate }}>
      {children}
    </FeaturesContext.Provider>
  );
};


